import React from 'react';
import { motion } from 'framer-motion';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white py-12 px-6">
      <div className="container mx-auto max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-5xl font-bold mb-4 text-gray-900">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Last updated: January 3, 2025</p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                Welcome to AVALW Marketplace ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our extension marketplace platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">2. Information We Collect</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">2.1 Personal Information</h3>
                  <p className="text-gray-700 leading-relaxed">
                    When you create an account, we collect:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                    <li>Email address</li>
                    <li>Username</li>
                    <li>Password (encrypted)</li>
                    <li>Developer information (if you become a developer)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">2.2 Extension Data</h3>
                  <p className="text-gray-700 leading-relaxed">
                    When you upload extensions, we collect source code, manifest files, and related metadata. Your source code is compiled into binary .avalw format and is not publicly accessible.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">2.3 Usage Data</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We automatically collect information about how you interact with our platform, including:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                    <li>Pages visited</li>
                    <li>Extensions downloaded</li>
                    <li>Browser type and version</li>
                    <li>IP address (anonymized)</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">3. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use your information to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Provide and maintain the AVALW Marketplace platform</li>
                <li>Process extension submissions and reviews</li>
                <li>Compile source code into secure .avalw binary format</li>
                <li>Send important updates about your extensions or account</li>
                <li>Improve our services and user experience</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">4. Source Code Protection</h2>
              <p className="text-gray-700 leading-relaxed">
                Your extension source code is treated as highly confidential. We:
              </p>
              <ul className="list-disc list-inside text-gray-700 mt-4 space-y-2">
                <li>Compile your source code into proprietary binary format</li>
                <li>Never share your source code with third parties</li>
                <li>Store source code with encryption at rest</li>
                <li>Only allow authorized build system access</li>
                <li>Delete source code upon your request</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">5. Data Sharing and Disclosure</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do not sell your personal information. We may share information only in these cases:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>With your consent:</strong> When you explicitly agree to share information</li>
                <li><strong>Legal requirements:</strong> When required by law or legal process</li>
                <li><strong>Service providers:</strong> With trusted partners who assist in operating our platform (under strict confidentiality agreements)</li>
                <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">6. Data Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We implement industry-standard security measures including:
              </p>
              <ul className="list-disc list-inside text-gray-700 mt-4 space-y-2">
                <li>SSL/TLS encryption for data in transit</li>
                <li>AES-256 encryption for sensitive data at rest</li>
                <li>Regular security audits and penetration testing</li>
                <li>Multi-factor authentication support</li>
                <li>Access controls and monitoring</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">7. Your Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Export your data</li>
                <li>Opt-out of marketing communications</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">8. Cookies and Tracking</h2>
              <p className="text-gray-700 leading-relaxed">
                We use essential cookies to maintain your session and preferences. We do not use third-party advertising cookies. You can disable cookies in your browser settings, but this may affect platform functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">9. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your data for as long as your account is active or as needed to provide services. Extension source code is retained for build purposes but can be deleted upon request. Compiled .avalw files are retained as long as the extension is published.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">10. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our platform is not intended for users under 13 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">11. International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Your data may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">12. Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by email or through a notice on our platform. Your continued use of the platform after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">13. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have questions about this Privacy Policy or wish to exercise your rights, contact us at:
              </p>
              <div className="mt-4 p-6 bg-gray-50 rounded-lg border-2 border-gray-200">
                <p className="text-gray-900 font-semibold">Email: privacy@avalw.com</p>
                <p className="text-gray-700 mt-2">Data Protection Officer: dpo@avalw.com</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
